import { getClientAuthProviderConfig } from '@/lib/auth-provider';
import { getEntraAccessToken } from '@/lib/msal';

import { measuresManager } from './measuresManager';

const BASE_URL_DATA = `${process.env.NEXT_PUBLIC_OKAHU_API_URL}/${process.env.NEXT_PUBLIC_OKAHU_API_VERSION}`;
const MOCK_BASE_URL_DATA = `${process.env.NEXT_PUBLIC_MOCK_API_URL}/${process.env.NEXT_PUBLIC_OKAHU_API_VERSION}`;
const BASE_URL_MGMT = `${process.env.NEXT_PUBLIC_OKAHU_MGMT_API_URL}${process.env.NEXT_PUBLIC_OKAHU_MGMT_API_VERSION}`;
const BASE_URL_ML_ANALYSIS = `${process.env.NEXT_PUBLIC_OKAHU_ML_ANALYSIS_API_URL}${process.env.NEXT_PUBLIC_OKAHU_ML_ANALYSIS_API_VERSION}`;

const BASE_URL_SRE_AGENT = `${process.env.NEXT_PUBLIC_OKAHU_SRE_AGENT_API_URL}${process.env.NEXT_PUBLIC_OKAHU_SRE_AGENT_API_VERSION}`;

const BASE_URL_EVALS = `${process.env.NEXT_PUBLIC_EVALS_API_URL}${process.env.NEXT_PUBLIC_EVALS_API_VERSION}`;
const BASE_URL_NARRATIVE = `${process.env.NEXT_PUBLIC_NARRATIVE_GRAPH_API_URL}${process.env.NEXT_PUBLIC_NARRATIVE_GRAPH_API_VERSION}`;
const MAX_LOG_IN_TIME = process.env.NEXT_PUBLIC_OKAHU_MAX_LOG_IN_TIME ?? '24';

const getClientAuthConfig = () => getClientAuthProviderConfig();

let url = BASE_URL_DATA;

interface ReqParams {
  endpoint: string;
  queryParams?: { [key: string]: string };
}

export const LOGGEDIN_OKAHU_USER = 'USER_SESSION_START';

let refreshTokenPromise: Promise<any> | null = null;
let cachedSession: any = null;
let accessTokenExpiresAt: number | null = null;
let accessToken: { accessToken: string } | null = null;

export const genUrl = (
  endpoint: string,
  queryParams?: { [key: string]: string },
  mgmt?: boolean,
  mlAnalysis?: boolean,
  evals?: boolean,
  sreAgent?: boolean,
  narrativeGraph?: boolean
): string => {
  // Construct query string from queryParams object
  const queryString = queryParams
    ? '?' + new URLSearchParams(queryParams).toString()
    : '';

  let baseUrl = url;
  let target = 'data';

  if (mgmt) {
    baseUrl = BASE_URL_MGMT;
    target = 'mgmt';
  } else if (mlAnalysis) {
    baseUrl = BASE_URL_ML_ANALYSIS;
    target = 'ml';
  } else if (sreAgent) {
    baseUrl = BASE_URL_SRE_AGENT;
    target = 'sre';
  } else if (evals) {
    baseUrl = BASE_URL_EVALS;
    target = 'evals';
  } else if (narrativeGraph) {
    baseUrl = BASE_URL_NARRATIVE;
    target = 'narrative';
  }

  if (getClientAuthConfig().usesProxy) {
    return `/api/proxy/${target}/${endpoint}` + queryString;
  }

  // return the full url
  return `${baseUrl}/${endpoint}` + queryString;
};

const getAccessToken = async (grant = false) => {
  try {
    const res = await fetch(`/api/access-token?grant=${grant}`);
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    const token = await res.json();
    accessTokenExpiresAt = getTokenExpiry(token);
    accessToken = token;
    return token;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const getTokenExpiry = (token: { accessToken: string }): number | null => {
  try {
    // JWT has 3 parts: header.payload.signature
    const parts = token.accessToken.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];

    // Base64 decode (handle URL-safe base64)
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));

    // Parse JSON
    const parsed = JSON.parse(decoded);

    // Return exp claim (Unix timestamp in seconds)
    return parsed.exp || null;
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

const ensureAccessToken = async (grant?: boolean) => {
  if (getClientAuthConfig().serverManagedToken) return null;

  const timeNow = Date.now();
  const loginTime = window.localStorage.getItem(`${LOGGEDIN_OKAHU_USER}`);

  const loginTimeElapsedInSec = (timeNow - parseInt(loginTime!)) / 1000;
  // timeToLogout = hh*mm*ss
  const timeToLogout = parseInt(MAX_LOG_IN_TIME) * 60 * 60;

  // this if block checks if the login time has exceeded the max time for user to stay logged in
  if (
    Number.isNaN(loginTimeElapsedInSec) ||
    loginTimeElapsedInSec < timeToLogout
  ) {
    if (
      accessTokenExpiresAt !== null &&
      timeNow > accessTokenExpiresAt * 1000
    ) {
      if (!refreshTokenPromise) {
        refreshTokenPromise = getAccessToken(grant);
      }

      // Wait for the existing refreshTokenPromise to resolve
      const newToken = await refreshTokenPromise;
      refreshTokenPromise = null;
      return newToken;
    }
  } else {
    console.log(`User logout: ${timeToLogout / 3600} hours has elapsed`);
    window.location.href = '/api/auth/logout';
    window.localStorage.removeItem(`${LOGGEDIN_OKAHU_USER}`);
    window.localStorage.removeItem(`accessToken`);
    window.localStorage.removeItem('measuresList');
    localStorage.removeItem('measuresLastFetch');
    measuresManager.destroy(); // Stop the interval
    localStorage.removeItem('appsList');
  }

  return null; // no new token required
};

const getSession = async () => {
  if (getClientAuthConfig().serverManagedToken) {
    return { accessToken: 'server-managed' };
  }

  try {
    const res = await fetch('/api/session');
    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }
    const session = await res.json();
    accessTokenExpiresAt = session?.accessTokenExpiresAt;
    return session;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const updateOptions = async (options: RequestInit, body?: any) => {
  if (getClientAuthConfig().serverManagedToken) {
    // No Authorization header - the proxy attaches the shared token server-side. Content-Type
    // still must be declared: fetch() would label the body "text/plain", and FastAPI only parses
    // a declared model from application/json, so endpoints with a body model 422.
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(options.method!) && body) {
      options.body = JSON.stringify(body);
      return {
        ...options,
        headers: { 'Content-Type': 'application/json' },
      };
    }
    return options;
  }

  const token: string = getClientAuthConfig().browserManagedSession
    ? await getEntraAccessToken()
    : accessToken?.accessToken || cachedSession.accessToken;
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  };

  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(options.method!) && body) {
    headers['Content-Type'] = 'application/json';
    options.body = JSON.stringify(body);
  }

  return { ...options, headers };
};

const genOptions = async (options: RequestInit, body?: any) => {
  switch (options.method) {
    case 'GET':
      return await updateOptions(options, undefined);
    case 'DELETE':
    case 'POST':
    case 'PUT':
    case 'PATCH':
      return await updateOptions(options, body);
    default:
      throw new Error(
        'Method not available. Available methods are GET, POST, PUT, PATCH and DELETE'
      );
  }
};

const makeRequest = async (url: string, options: RequestInit) => {
  try {
    const res = await fetch(url, options);
    const contentType = res.headers.get('content-type');
    let data;

    if (contentType?.includes('application/json')) {
      try {
        data = await res.json();
      } catch (jsonError) {
        throw new Error('Failed to parse JSON response.');
      }
    } else {
      data = await res.text(); // Fallback for non-JSON responses
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('[API response]', {
        method: options.method,
        url,
        status: res.status,
        ok: res.ok,
        data,
      });
    }

    if (!res.ok) {
      throw {
        status: res.status,
        statusText: res.statusText,
        data, // Now includes the parsed data (either JSON or text)
      };
    }

    return data;
  } catch (error) {
    console.error(`Error fetching from ${url}`, error);
    throw error;
  }
};

// this function checks whether the incoming request is of ReqParams type or
// ReqParams[] and accordingly processes the request
const processReq = async (
  req: ReqParams | ReqParams[],
  options: RequestInit,
  body?: any,
  mgmt?: boolean,
  grant?: boolean,
  mlAnalysis?: boolean,
  evals?: boolean,
  sreAgent?: boolean,
  narrativeGraph?: boolean
) => {
  if (!getClientAuthConfig().browserManagedSession) {
    if (!cachedSession || grant === true) {
      cachedSession = await getSession();
    }
    await ensureAccessToken(grant);
  }

  const reqArr = Array.isArray(req) ? req : [req];

  // map over the endpoints array and make an array of requests
  const requests = reqArr.map(async (each: any) => {
    const { endpoint, queryParams } = each;

    const url = mlAnalysis
      ? genUrl(endpoint, queryParams, false, true)
      : sreAgent
        ? genUrl(endpoint, queryParams, false, false, false, true)
        : evals
          ? genUrl(endpoint, queryParams, false, false, true)
          : narrativeGraph
            ? genUrl(endpoint, queryParams, false, false, false, false, true)
            : mgmt
              ? genUrl(endpoint, queryParams, true)
              : genUrl(endpoint, queryParams);
    const updatedOptions: RequestInit = await genOptions(options, body);

    // wait for all the requests to complete and return the promises array
    return makeRequest(url, updatedOptions!);
  });

  // wait for all the requests to complete and return the promises array
  return Promise.all(requests);
};

/**
 * Ensures session + access token are valid and returns the auth headers
 * for an outgoing request. Exported so streaming requests (which bypass
 * the buffered `request` helper) can reuse the same auth flow.
 */
export const getAuthHeaders = async (
  grant?: boolean
): Promise<Record<string, string>> => {
  if (getClientAuthConfig().serverManagedToken) return {};

  if (getClientAuthConfig().browserManagedSession) {
    return { Authorization: `Bearer ${await getEntraAccessToken()}` };
  }

  if (!cachedSession || grant === true) {
    cachedSession = await getSession();
  }
  await ensureAccessToken(grant);
  const token: string = accessToken?.accessToken || cachedSession.accessToken;
  return { Authorization: `Bearer ${token}` };
};

/**
 *  Request object to make API calls to different HTTP methods (GET, POST, PUT, PATCH and DELETE).
 */
export const request = {
  get: async (
    req: ReqParams | ReqParams[],
    grant?: boolean,
    mlAnalysis?: boolean,
    evals?: boolean,
    narrativeGraph?: boolean,
    sreAgent?: boolean,
    signal?: AbortSignal
  ) => {
    const options: RequestInit = {
      method: 'GET',
      signal,
    };

    return processReq(
      req,
      options,
      undefined,
      undefined,
      grant,
      mlAnalysis,
      evals,
      sreAgent,
      narrativeGraph
    );
  },

  put: async (
    req: ReqParams | ReqParams[],
    body?: any,
    signal?: AbortSignal
  ) => {
    const options: RequestInit = {
      method: 'PUT',
      signal,
    };

    return processReq(req, options, body);
  },

  post: async (
    req: ReqParams | ReqParams[],
    body?: any,
    mgmt?: boolean,
    evals?: boolean,
    sreAgent?: boolean,
    signal?: AbortSignal
  ) => {
    const options: RequestInit = {
      method: 'POST',
      signal,
    };

    return processReq(
      req,
      options,
      body,
      mgmt,
      undefined,
      undefined,
      evals,
      sreAgent
    );
  },

  patch: async (
    req: ReqParams | ReqParams[],
    body?: any,
    signal?: AbortSignal
  ) => {
    const options: RequestInit = {
      method: 'PATCH',
      signal,
    };

    return processReq(req, options, body);
  },

  delete: async (
    req: ReqParams | ReqParams[],
    body?: any,
    signal?: AbortSignal
  ) => {
    const options = {
      method: 'DELETE',
      signal,
    };

    return processReq(req, options, body);
  },
  /**
   * Fetches the platform health endpoint. This is a public endpoint that lives
   * at `${host}/api/health` (note: no `/api/v1` version prefix), so it targets
   * the URL directly and bypasses `processReq`/`genUrl` and the auth pipeline,
   * while still reusing `makeRequest` for consistent error/JSON handling.
   */
  health: async (signal?: AbortSignal) => {
    const healthUrl = `${process.env.NEXT_PUBLIC_OKAHU_API_URL}/health`;
    return makeRequest(healthUrl, { method: 'GET', signal });
  },
  setupMock: () => {
    url = MOCK_BASE_URL_DATA;
  },
  setupMockTearDown: () => {
    url = BASE_URL_DATA;
  },
};
